import { EdgeType, LinkNegation, LinkQualifier } from '../model/edge-model';

/**
 * 连线修饰配置：为不同 EdgeType 提供 Condition/Event 与 Not 的可选组合。
 * - qualifierOptions：可选的修饰类型列表
 * - negationOptions：可选的否定选项列表
 * - allowedNegationsByQualifier：针对每个修饰类型允许的否定取值
 *   （未配置的修饰类型默认使用 negationOptions 全量）
 */
export interface EdgeMarkerOption {
  qualifierOptions: LinkQualifier[];
  negationOptions: LinkNegation[];
  allowedNegationsByQualifier?: Partial<Record<LinkQualifier, LinkNegation[]>>;
  defaultQualifier?: LinkQualifier;
  defaultNegation?: LinkNegation;
}

// 通用：可选修饰 + 可选否定（事件修饰不允许取 Not，避免语义矛盾）
const qualifierAndNegationOption: EdgeMarkerOption = {
  qualifierOptions: [
    LinkQualifier.None,
    LinkQualifier.Condition,
    LinkQualifier.Event,
  ],
  negationOptions: [LinkNegation.None, LinkNegation.Not],
  allowedNegationsByQualifier: {
    [LinkQualifier.Event]: [LinkNegation.None],
  },
  defaultQualifier: LinkQualifier.None,
  defaultNegation: LinkNegation.None,
};

// Agent：只允许修饰，不允许否定
const agentQualifierOnly: EdgeMarkerOption = {
  qualifierOptions: [
    LinkQualifier.None,
    LinkQualifier.Condition,
    LinkQualifier.Event,
  ],
  negationOptions: [LinkNegation.None, LinkNegation.Not],
  allowedNegationsByQualifier: {
    [LinkQualifier.Event]: [LinkNegation.None],
  },
  defaultQualifier: LinkQualifier.None,
  defaultNegation: LinkNegation.None,
};

// 仅可选择修饰，不允许否定（消耗/影响）
const qualifierOnlyOption: EdgeMarkerOption = {
  qualifierOptions: [
    LinkQualifier.None,
    LinkQualifier.Condition,
    LinkQualifier.Event,
  ],
  negationOptions: [LinkNegation.None],
  defaultQualifier: LinkQualifier.None,
  defaultNegation: LinkNegation.None,
};

export const edgeMarkerConfig: Partial<Record<EdgeType, EdgeMarkerOption>> = {
  // Agent / Instrument：可选修饰与否定
  [EdgeType.Agent]: agentQualifierOnly,
  [EdgeType.Instrument]: qualifierAndNegationOption,
  // 消耗 / 影响：仅可选修饰
  [EdgeType.Consumption]: qualifierOnlyOption,
  [EdgeType.Effect]: qualifierOnlyOption,
  // 其他 EdgeType 按需补充，保持可扩展
};

export const getEdgeMarkerOption = (
  edgeType: EdgeType | null
): EdgeMarkerOption | null => {
  if (!edgeType) return null;
  return edgeMarkerConfig[edgeType] || null;
};

export const getAllowedNegations = (
  option: EdgeMarkerOption,
  qualifier: LinkQualifier
): LinkNegation[] => {
  if (option.allowedNegationsByQualifier?.[qualifier]) {
    return option.allowedNegationsByQualifier[qualifier] as LinkNegation[];
  }
  return option.negationOptions;
};

const qualifierSymbols: Record<LinkQualifier, string> = {
  [LinkQualifier.None]: '',
  [LinkQualifier.Condition]: 'c',
  [LinkQualifier.Event]: 'e',
};

const negationSymbols: Record<LinkNegation, string> = {
  [LinkNegation.None]: '',
  [LinkNegation.Not]: '¬',
};

export const buildMarkerLabel = (
  qualifier: LinkQualifier,
  negation: LinkNegation
): string => {
  const neg = negationSymbols[negation];
  const qual = qualifierSymbols[qualifier];
  if (!neg && !qual) return '';
  if (neg && qual) return `${neg} ${qual}`;
  return neg || qual;
};
